import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, SelectPanel } from "@primer/react";
import { TriangleDownIcon, XCircleFillIcon } from "@primer/octicons-react";
import { useDispatch, useSelector } from "react-redux";
import { saleSetKhoanNopGroupTypeApi } from "../../../api/saleSetKhoanNopGroupTypeApi";
import { sf_hinhthucnopbom } from "../../../models/response/category/sf_hinhthucnopbom";
import BtnButton from "../../ui/button";
import ModalActions from "../../ui/modal/ModalActions";
import MyModal from "../../ui/modal/MyModal";
import { showNotify } from "../../../helpers/toast";
import { RootState } from "../../../state/reducers";
import { loadKhoanNopGroupStart } from "../../../state/actions/khoanNopGroupActions";

const KY_NOP_TYPES = [
  { sf_saleset_register_type_id: 10, label: "Theo năm" },
  { sf_saleset_register_type_id: 5, label: "Theo kỳ" },
  { sf_saleset_register_type_id: 13, label: "Theo tháng" },
];

export const ALL_HTNBS: sf_hinhthucnopbom[] = [
  { id: 1, name: "Cả năm", is_active: true, thangs: "0,8,9,10,11,12,1,2,3,4,5", sf_saleset_register_type_id: 10, dm_coso_id: 0 },
  { id: 2, name: "Học kỳ 1", is_active: true, thangs: "0,8,9,10,11,12", sf_saleset_register_type_id: 5, dm_coso_id: 0 },
  { id: 3, name: "Học kỳ 2", is_active: true, thangs: "0,1,2,3,4,5", sf_saleset_register_type_id: 5, dm_coso_id: 0 },
  { id: 9, name: "Tháng 8", is_active: true, thangs: "8", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 10, name: "Tháng 9", is_active: true, thangs: "9", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 11, name: "Tháng 10", is_active: true, thangs: "10", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 12, name: "Tháng 11", is_active: true, thangs: "11", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 13, name: "Tháng 12", is_active: true, thangs: "12", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 14, name: "Tháng 1", is_active: true, thangs: "1", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 15, name: "Tháng 2", is_active: true, thangs: "2", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 16, name: "Tháng 3", is_active: true, thangs: "3", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 17, name: "Tháng 4", is_active: true, thangs: "4", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 18, name: "Tháng 5", is_active: true, thangs: "5", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 7, name: "Tháng 6", is_active: true, thangs: "6", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
  { id: 8, name: "Tháng 7", is_active: true, thangs: "7", sf_saleset_register_type_id: 13, dm_coso_id: 0 },
];

const HTNB_ITEMS = ALL_HTNBS.map((h) => ({ id: h.id, text: h.name, sf_saleset_register_type_id: h.sf_saleset_register_type_id }));

const HTNBS_BY_TYPE = new Map<number, typeof HTNB_ITEMS>(
  KY_NOP_TYPES.map((t) => [
    t.sf_saleset_register_type_id,
    HTNB_ITEMS.filter((h) => h.sf_saleset_register_type_id === t.sf_saleset_register_type_id),
  ])
);

type GroupInfo = {
  groupKey: string;
  sf_khoannop_group_id: number;
  ten_khoan_nop_group: string;
};

export type GroupHtnbFilter = Map<string, number[]>;

type LocHinhThucNopModalProps = {
  rawDataSource: any[];
  sf_hinhthucnopbom_ids?: string;
  dm_coso_id: number;
  ts_hocsinh_id: number;
  nam_hoc: string;
  initialSelection: GroupHtnbFilter;
  onApply: (selection: GroupHtnbFilter) => void;
  onClose: () => void;
};

export const LocHinhThucNopModal = (props: LocHinhThucNopModalProps) => {
  const dispatch = useDispatch();
  const { dm_coso_id, nam_hoc: param_nam_hoc } = useSelector((x: RootState) => x.common);
  const { sf_khoannop_groups } = useSelector((x: RootState) => x.khoanNopGroup);

  React.useEffect(() => {
    dispatch(loadKhoanNopGroupStart());
  }, [dispatch]);

  const [hocSinhGroupTypes, setHocSinhGroupTypes] = useState<any[]>([]);

  React.useEffect(() => {
    const loadHocSinhGroupTypes = async () => {
      if (!props.ts_hocsinh_id || !props.nam_hoc) return;
      const res = await saleSetKhoanNopGroupTypeApi.select_by_hocsinh({
        nam_hoc: props.nam_hoc,
        ts_hocsinh_id: props.ts_hocsinh_id,
      });
      if (res.is_success && res.data) {
        setHocSinhGroupTypes(res.data);
      }
    };
    loadHocSinhGroupTypes();
  }, [props.ts_hocsinh_id, props.nam_hoc]);

  const initialSelectionFromData = useMemo((): GroupHtnbFilter => {
    if (props.initialSelection && props.initialSelection.size > 0) {
      return new Map(props.initialSelection);
    }

    if (!sf_khoannop_groups || sf_khoannop_groups.length === 0) {
      return new Map();
    }

    if (hocSinhGroupTypes.length === 0) {
      return new Map();
    }

    const initialMap = new Map<string, number[]>();
    const groupsData = (sf_khoannop_groups || [])
      .filter((x: any) => x.dm_coso_id === dm_coso_id && (x.is_dichvu || x.is_dichvu_fixed));

    groupsData.forEach((dv: any) => {
      const groupId = dv.id;
      const groupKey = String(groupId);

      const registeredType = hocSinhGroupTypes.find(
        (x) => x.sf_khoannop_group_id === groupId
      );

      if (registeredType) {
        const typeId = registeredType.sf_saleset_register_type_id;
        let selectedIds: number[] = [];

        // Lấy tháng hiện tại (1-12)
        const currentMonth = new Date().getMonth() + 1;

        // Mapping từ số tháng sang id trong ALL_HTNBS
        const monthToId: Record<number, number> = {
          8: 9, 9: 10, 10: 11, 11: 12, 12: 13,
          1: 14, 2: 15, 3: 16, 4: 17, 5: 18, 6: 7, 7: 8
        };

        if (typeId === 10) {
          selectedIds = [1];
        } else if (typeId === 5) {
          // Theo kỳ: dựa vào tháng hiện tại để xác định kỳ
          // Học kỳ 1: tháng 8-12, Học kỳ 2: tháng 1-7
          if (currentMonth >= 8 && currentMonth <= 12) {
            selectedIds = [2]; // Học kỳ 1
          } else {
            selectedIds = [3]; // Học kỳ 2
          }
        } else if (typeId === 13) {
          // Theo tháng: chỉ chọn tháng hiện tại
          selectedIds = [monthToId[currentMonth]];
        }

        if (selectedIds.length > 0) {
          initialMap.set(groupKey, selectedIds);
        }
      }
    });

    return initialMap;
  }, [sf_khoannop_groups, hocSinhGroupTypes, dm_coso_id, props.initialSelection]);

  const [selection, setSelection] = useState<GroupHtnbFilter>(new Map());

  useEffect(() => {
    if (props.initialSelection && props.initialSelection.size > 0) {
      setSelection(new Map(props.initialSelection));
      return;
    }
    if (initialSelectionFromData.size > 0) {
      setSelection(initialSelectionFromData);
    }
  }, [initialSelectionFromData, props.initialSelection]);

  const [openPanel, setOpenPanel] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const groups = useMemo((): GroupInfo[] => {
    // Lọc chỉ hiển thị những group mà học sinh đã đăng ký
    const registeredGroupIds = hocSinhGroupTypes.map(x => x.sf_khoannop_group_id);
    return (sf_khoannop_groups || [])
      .filter((x: any) =>
        x.dm_coso_id === dm_coso_id &&
        (x.is_dichvu || x.is_dichvu_fixed) &&
        registeredGroupIds.includes(x.id)
      )
      .map((dv: any) => ({
        groupKey: String(dv.id),
        sf_khoannop_group_id: dv.id,
        ten_khoan_nop_group: dv.ten_khoan_nop_group || dv.ma_khoan_nop_group || String(dv.id),
      }));
  }, [sf_khoannop_groups, dm_coso_id, hocSinhGroupTypes]);

  const getSelectedTypeId = (groupKey: string): number | null => {
    const ids = selection.get(groupKey) || [];
    if (ids.length === 0) return null;
    return HTNB_ITEMS.find((h) => h.id === ids[0])?.sf_saleset_register_type_id ?? null;
  };

  const handleSelectedChange = (
    groupKey: string,
    typeId: number,
    newItems: { id: number; text: string }[]
  ) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const newIds = newItems.map((x) => x.id);
      if (newIds.length === 0) {
        next.delete(groupKey);
      } else {
        next.set(groupKey, newIds);
      }
      return next;
    });
  };

  const handleClear = (groupKey: string) => {
    setSelection((prev) => {
      const next = new Map(prev);
      next.delete(groupKey);
      return next;
    });
  };

  const handleApply = () => {
    props.onApply(selection);
    props.onClose();
  };

  const handleApplyAndSave = async () => {
    setIsSaving(true);
    const list_khoannop_group = groups
      .filter((g) => g.sf_khoannop_group_id)
      .map((g) => {
        const selectedIds = selection.get(g.groupKey) || [];
        const selectedTypeId =
          selectedIds.length > 0
            ? (HTNB_ITEMS.find((h) => h.id === selectedIds[0])?.sf_saleset_register_type_id ?? 0)
            : 0;
        return {
          sf_khoannop_group_id: g.sf_khoannop_group_id || 0,
          sf_saleset_register_type_id: selectedTypeId,
          is_checked: selectedIds.length > 0,
        };
      });

    const dataInsert = {
      list_khoannop_group,
      ts_hocsinh_id: props.ts_hocsinh_id,
      nam_hoc: param_nam_hoc,
    };
    const res = await saleSetKhoanNopGroupTypeApi.insert_multiple(dataInsert);
    setIsSaving(false);

    if (res.is_success) {
      showNotify({ message: "Cập nhật thành công", type: "success" });
      props.onApply(selection);
      props.onClose();
    } else {
      showNotify({
        message: res.message || "Có lỗi, vui lòng kiểm tra lại",
        type: "error",
      });
    }
  };

  const headerStyle: React.CSSProperties = {
    border: "1px solid #d0d7de",
    padding: "8px 10px",
    background: "#f6f8fa",
    fontWeight: 600,
    textAlign: "center",
    fontSize: 13,
  };
  const cellStyle: React.CSSProperties = {
    border: "1px solid #d0d7de",
    padding: "6px 8px",
    verticalAlign: "middle",
  };

  return (
    <MyModal
      isOpen={true}
      onClose={props.onClose}
      title="Lọc dữ liệu"
      width="60%"
    >
      <Box sx={{ p: 2, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, textAlign: "left", width: "28%" }}>Dịch vụ</th>
              {KY_NOP_TYPES.map((type) => (
                <th key={type.sf_saleset_register_type_id} style={{ ...headerStyle, width: "24%" }}>
                  {type.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group, idx) => {
              const selectedTypeId = getSelectedTypeId(group.groupKey);
              const selectedIds = selection.get(group.groupKey) || [];

              return (
                <tr key={group.groupKey} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td style={{ ...cellStyle, fontWeight: 500 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span>{group.ten_khoan_nop_group}</span>
                      {selectedIds.length > 0 && (
                        <Button
                          variant="invisible"
                          size="small"
                          trailingVisual={XCircleFillIcon}
                          sx={{ color: "danger.emphasis", px: 1 }}
                          onClick={() => handleClear(group.groupKey)}
                        >
                          Xóa
                        </Button>
                      )}
                    </Box>
                  </td>

                  {KY_NOP_TYPES.map((type) => {
                    const typeId = type.sf_saleset_register_type_id;
                    const items = HTNBS_BY_TYPE.get(typeId) || [];
                    const isDisabled = selectedTypeId !== null && selectedTypeId !== typeId;
                    const panelKey = `${group.groupKey}_${typeId}`;
                    const isOpen = openPanel === panelKey;

                    const selectedForType = items.filter((h) => selectedIds.includes(h.id));
                    const anchorLabel =
                      selectedForType.length === 0
                        ? `Chọn...`
                        : selectedForType.length <= 2
                          ? selectedForType.map((x) => x.text).join(", ")
                          : `${selectedForType.length} đã chọn`;

                    return (
                      <td key={typeId} style={{ ...cellStyle }}>
                        <SelectPanel
                          renderAnchor={({ "aria-labelledby": ariaLabelledBy, ...anchorProps }) => (
                            <Button
                              size="small"
                              trailingAction={TriangleDownIcon}
                              aria-labelledby={ariaLabelledBy}
                              disabled={isDisabled}
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "12px",
                                opacity: isDisabled ? 0.4 : 1,
                              }}
                              {...anchorProps}
                            >
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {anchorLabel}
                              </span>
                            </Button>
                          )}
                          title={type.label}
                          open={isOpen && !isDisabled}
                          onOpenChange={(o) => setOpenPanel(o ? panelKey : null)}
                          items={items}
                          selected={selectedForType}
                          onSelectedChange={(newItems: any[]) =>
                            handleSelectedChange(group.groupKey, typeId, newItems)
                          }
                          onFilterChange={() => { }}
                          showItemDividers={true}
                          overlayProps={{ width: "medium", height: "auto" }}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {groups.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", padding: "20px", color: "#6e7781", border: "1px solid #d0d7de" }}
                >
                  Không có dữ liệu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      <ModalActions>
        <BtnButton variant="default" onClick={props.onClose}>
          Đóng
        </BtnButton>
        <BtnButton variant="primary" onClick={handleApply} disabled={selection.size === 0}>
          Áp dụng
        </BtnButton>
        <BtnButton variant="primary" onClick={handleApplyAndSave} isLoading={isSaving} disabled={selection.size === 0}>
          Áp dụng và lưu hình thức nộp
        </BtnButton>
      </ModalActions>
    </MyModal>
  );
};
